import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Download, FileText, ChevronDown, Layers, Shield, Leaf, Gem, LayoutGrid, Palette, PlayCircle, Play } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function SmartPanelPage() {
  const {
    t
  } = useLanguage();
  const [openMaterial, setOpenMaterial] = useState<string | null>(null);
  const advantages = [t('panel.adv1'), t('panel.adv2'), t('panel.adv3'), t('panel.adv4'), t('panel.adv5'), t('panel.adv6')];
  return <div className="w-full bg-white pb-32" data-id="element-775">
      <div className="bg-corporate-dark text-white py-24 md:py-40 px-4 relative overflow-hidden" data-id="element-776">
        <div className="absolute inset-0 opacity-20" data-id="element-777">
          <img src="https://cdn.magicpatterns.com/uploads/pA5tdaan1tKmFkj2e61XXG/SmartPanel_(paneles_decorativos.png" alt="Background" className="w-full h-full object-cover" data-id="element-778" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10 mt-10" data-id="element-779">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-780">
            <span className="text-accent-beige font-medium tracking-[0.2em] uppercase text-sm mb-6 block" data-id="element-781">
              {t('prod.line')}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 tracking-tight" data-id="element-782">
              SmartPanel
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-2xl font-light leading-relaxed" data-id="element-783">
              {t('panel.hero.desc')}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28" data-id="element-784">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20" data-id="element-785">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-786">
            <h2 className="text-4xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-787">
              {t('panel.sec.title')}
            </h2>
            <div className="text-lg text-gray-600 font-light leading-relaxed space-y-6 mb-12" data-id="element-788">
              <p data-id="element-789">{t('panel.sec.p1')}</p>
              <p data-id="element-790">{t('panel.sec.p2')}</p>
            </div>

            <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-791">
              {t('prod.adv.title')}
            </h3>
            <ul className="space-y-5 mb-12" data-id="element-792">
              {advantages.map((adv, idx) => <li key={idx} className="flex items-start" data-id="element-793">
                  <CheckCircle2 className="h-6 w-6 text-corporate-blue mr-4 flex-shrink-0 mt-0.5" data-id="element-794" />
                  <span className="text-gray-700 text-lg font-light" data-id="element-795">
                    {adv}
                  </span>
                </li>)}
            </ul>

            <a href="#materiales-smartpanel" onClick={e => {
            e.preventDefault();
            document.getElementById('materiales-smartpanel')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }} className="inline-flex items-center px-8 py-4 bg-neutral-grey text-corporate-dark font-medium rounded-md hover:bg-gray-200 hover:scale-[1.02] transition-all duration-500 ease-premium" data-id="element-796">
              <Download className="mr-3 h-5 w-5" data-id="element-797" />
              {t('prod.dl.btn')}
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="space-y-8" data-id="element-798">
            <div className="h-96 bg-gray-100 rounded-sm overflow-hidden group" data-id="element-799">
              <img src="https://cdn.magicpatterns.com/uploads/pA5tdaan1tKmFkj2e61XXG/SmartPanel_(paneles_decorativos.png" alt="Aplicación SmartPanel" className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-800" />
            </div>
            <div className="grid grid-cols-2 gap-8" data-id="element-801">
              <div className="bg-neutral-grey p-8 rounded-sm hover:shadow-md transition-all duration-500 ease-premium" data-id="element-802">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-803">
                  {t('panel.app1.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-804">
                  {t('panel.app1.desc')}
                </p>
              </div>
              <div className="bg-neutral-grey p-8 rounded-sm hover:shadow-md transition-all duration-500 ease-premium" data-id="element-805">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-806">
                  {t('panel.app2.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-807">
                  {t('panel.app2.desc')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Materiales SmartPanel */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} id="materiales-smartpanel" className="mt-24 lg:mt-32 scroll-mt-24" data-id="element-808">
          <h3 className="text-3xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-809">
            {t('prod.docs.panel.title')}
          </h3>
          <p className="text-lg text-gray-500 font-light mb-12 max-w-3xl" data-id="element-810">
            {t('prod.docs.panel.materials.desc')}
          </p>

          <div className="space-y-4" data-id="element-811">
            {[{
            id: 'principal',
            name: t('panel.main.name'),
            desc: t('panel.main.desc'),
            icon: LayoutGrid,
            categories: []
          }, {
            id: 'bambu',
            name: t('panel.bambu.name'),
            desc: t('panel.bambu.desc'),
            icon: Leaf,
            categories: [{
              title: t('prod.docs.catalogs'),
              docs: [{
                name: t('panel.bambu.cat1'),
                url: 'https://hangzhoutucegongfang.yunzhan365.com/books/sthb/mobile/index.html'
              }, {
                name: t('panel.bambu.cat2'),
                url: 'https://hangzhoutucegongfang.yunzhan365.com/books/ecsm/mobile/index.html'
              }, {
                name: t('panel.bambu.cat3'),
                url: 'https://hangzhoutucegongfang.yunzhan365.com/books/huxp/mobile/index.html'
              }, {
                name: t('panel.bambu.cat4'),
                url: 'https://book.yunzhan365.com/grwb/vewp/mobile/index.html'
              }, {
                name: t('panel.bambu.cat5'),
                url: 'https://hangzhoutucegongfang.yunzhan365.com/books/otqh/mobile/index.html'
              }, {
                name: t('panel.bambu.cat6'),
                url: 'https://hangzhoutucegongfang.yunzhan365.com/books/nlly/mobile/index.html'
              }, {
                name: t('panel.bambu.cat7'),
                url: 'https://hangzhoutucegongfang.yunzhan365.com/books/qgjx/mobile/index.html'
              }, {
                name: t('panel.bambu.cat8'),
                url: 'https://hangzhoutucegongfang.yunzhan365.com/books/gmtt/mobile/index.html'
              }, {
                name: t('panel.bambu.cat9'),
                url: 'https://drive.google.com/file/d/1-kfFzTD3JYTQrWghPFmMtWNEHAeoiqxT/view?usp=sharing'
              }, {
                name: t('panel.bambu.cat10'),
                url: 'https://drive.google.com/file/d/1-HC73fj1aF5xRdvNc0LxmVDjarAErntq/view?usp=sharing'
              }, {
                name: t('panel.bambu.cat12'),
                url: 'https://drive.google.com/file/d/1bA659OfmcHEQVlWO6IVH9BHFu87uS-Sa/view?usp=sharing',
                type: 'pdf'
              }]
            }, {
              title: t('prod.docs.techDocs'),
              docs: [{
                name: t('dl.panel.bambu.tech1'),
                url: 'https://drive.google.com/file/d/1hzPl1Wgem5X9gxbvSLnUYCAZqKiH5qBU/view?usp=sharing'
              }]
            }]
          }, {
            id: 'bambu-videos',
            name: 'SmartPanel Bambú Tableros – Vídeos de Pruebas',
            desc: 'Galería de demostraciones prácticas y pruebas de resistencia.',
            icon: PlayCircle,
            categories: []
          }, {
            id: 'pvc',
            name: t('panel.pvc.name'),
            desc: t('panel.pvc.desc'),
            icon: Layers,
            categories: [{
              title: t('prod.docs.catalogs'),
              docs: [{
                name: t('panel.pvc.cat1'),
                url: 'https://drive.google.com/file/d/1zr8o2ejc-nEwgTB62OZIu_X_ayO2GWZK/view?usp=sharing'
              }, {
                name: t('panel.pvc.cat2'),
                url: 'https://drive.google.com/file/d/1Ri56EHGHfsZxF4LEV6k0xUNoHy_M1E22/view?usp=sharing'
              }, {
                name: t('panel.pvc.cat3'),
                url: 'https://drive.google.com/file/d/11_qZZR20t8PjtozmHmgo0r7NYOemQXcy/view?usp=sharing'
              }, {
                name: t('panel.pvc.cat4'),
                url: 'https://drive.google.com/file/d/1oe1Ef-yW8F0_ohmeId5-LoxuRe9__-OP/view?usp=sharing'
              }, {
                name: t('panel.pvc.cat5'),
                url: 'https://drive.google.com/file/d/1J0QkA1QOaTREaXyNeHt5wFfI1_2yjAy8/view?usp=sharing'
              }]
            }, {
              title: t('prod.docs.techDocs'),
              docs: [{
                name: t('panel.pvc.tech1'),
                url: 'https://drive.google.com/file/d/1Fs7vu5F1J8R1EjXW-N16SbaoiKXWR7HT/view?usp=sharing'
              }]
            }]
          }, {
            id: 'piedra',
            name: t('panel.piedra.name'),
            desc: t('panel.piedra.desc'),
            icon: Gem,
            categories: [{
              title: t('prod.docs.catalogs'),
              docs: [{
                name: t('panel.piedra.cat1'),
                url: 'https://drive.google.com/file/d/1_jOnBvdvOFljR3HM4W2q2NLDG1Dltanq/view?usp=sharing'
              }, {
                name: t('panel.piedra.cat3'),
                url: 'https://drive.google.com/file/d/16neklZ7vYsWTIXS9r2BiomK4aSTGLw6r/view?usp=sharing'
              }, {
                name: t('panel.piedra.cat4'),
                url: 'https://drive.google.com/file/d/1ulYzoNpVQtnZy3dT8wJvbMJJdfIXeW_R/view?usp=sharing'
              }, {
                name: t('panel.piedra.cat5'),
                url: 'https://drive.google.com/file/d/1hE5uzaLrEOrBHnLa2--BYiPQMUrh9I-B/view?usp=sharing'
              }, {
                name: t('panel.piedra.cat6'),
                url: 'https://drive.google.com/file/d/1rj-9F5oYvcod-rJFUAoN7Oh3uNDx3NmR/view?usp=sharing'
              }]
            }]
          }].map(material => <div key={material.id} className="bg-white border border-gray-100 rounded-sm shadow-md overflow-hidden" data-id="element-812">
                <button onClick={() => setOpenMaterial(openMaterial === material.id ? null : material.id)} className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-neutral-grey transition-colors duration-300" data-id="element-813">
                  <div className="flex items-center" data-id="element-814">
                    <div className="w-10 h-10 bg-neutral-grey rounded-sm flex items-center justify-center mr-5 flex-shrink-0" data-id="element-815">
                      <material.icon className="h-5 w-5 text-corporate-blue" data-id="element-816" />
                    </div>
                    <div data-id="element-817">
                      <h4 className="text-xl font-heading font-bold text-corporate-dark tracking-tight" data-id="element-818">
                        {material.name}
                      </h4>
                      <p className="text-sm text-gray-500 font-light mt-1" data-id="element-819">
                        {material.desc}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-corporate-blue flex-shrink-0 ml-4 transition-transform duration-300 ${openMaterial === material.id ? 'rotate-180' : ''}`} data-id="element-820" />
                </button>
                <AnimatePresence initial={false} data-id="element-821">
                  {openMaterial === material.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.35,
                ease: [0.25, 0.1, 0.25, 1]
              }} className="overflow-hidden" data-id="element-822">
                      <div className="border-t border-gray-100" data-id="element-823">
                        {material.id === 'principal' && <div className="p-8 pb-0" data-id="element-824">
                            <h5 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-4 border-b border-gray-100 pb-2" data-id="element-825">
                              Vídeo de presentación SmartPanel
                            </h5>
                            <div className="flex justify-center mb-8" data-id="element-826">
                              <video src="https://cdn.magicpatterns.com/uploads/4eoJaLQy4q5CUFaSJZY7fU/Video%20.mp4" controls className="w-full max-w-sm rounded-md shadow-lg" style={{
                        maxHeight: '600px'
                      }} data-id="element-827" />
                            </div>
                          </div>}
                        {material.id === 'bambu-videos' && <div className="p-8" data-id="element-828">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-829">
                              {[{
                        title: '100% Impermeable',
                        desc: 'Demostración práctica de la resistencia al agua.',
                        url: 'https://www.instagram.com/reel/DXKZe0TCGWa/?igsh=ZXJ5ZXA4eHJqenYx'
                      }, {
                        title: 'Cómo se trabaja',
                        desc: 'Proceso de mecanizado e instalación.',
                        url: 'https://www.instagram.com/reel/DZAzVP0Iqfm/?igsh=MXQ0amxhbXFrcmEyZg%3D%3D'
                      }, {
                        title: 'Resistencia del material',
                        desc: 'Prueba de resistencia y durabilidad.',
                        url: 'https://www.instagram.com/reel/DYSfL38imFN/?igsh=MWR2eGdjcHMzMHVydg%3D%3D'
                      }, {
                        title: 'Arañazos y Antihuellas',
                        desc: 'Vídeo disponible próximamente.',
                        url: null
                      }].map((video, vIdx) => <div key={vIdx} className="bg-white border border-gray-100 rounded-md p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full group" data-id="element-830">
                                  <div className="w-12 h-12 bg-neutral-grey rounded-full flex items-center justify-center mb-4 group-hover:bg-corporate-blue transition-colors duration-300" data-id="element-831">
                                    <Play className="h-5 w-5 text-corporate-blue group-hover:text-white transition-colors duration-300 ml-1" data-id="element-832" />
                                  </div>
                                  <h4 className="text-lg font-heading font-bold text-corporate-dark mb-2" data-id="element-833">
                                    {video.title}
                                  </h4>
                                  <p className="text-sm text-gray-500 font-light mb-6 flex-1" data-id="element-834">
                                    {video.desc}
                                  </p>
                                  {video.url ? <a href={video.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-corporate-blue font-medium text-sm group-hover:underline" data-id="element-835">
                                      Ver vídeo <span className="ml-1" data-id="element-836">→</span>
                                    </a> : <span className="inline-flex items-center text-gray-400 font-medium text-sm cursor-default" data-id="element-837">
                                      Próximamente
                                    </span>}
                                </div>)}
                            </div>
                          </div>}
                        {material.categories.length > 0 ? <div className="p-8 space-y-8" data-id="element-838">
                            {material.categories.map((cat, catIdx) => <div key={catIdx} data-id="element-839">
                                <h5 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-4 border-b border-gray-100 pb-2" data-id="element-840">
                                  {cat.title}
                                </h5>
                                <div className="divide-y divide-gray-50" data-id="element-841">
                                  {cat.docs.map((doc, docIdx) => <div key={docIdx} className="flex flex-col sm:flex-row sm:items-center justify-between py-4 hover:bg-neutral-grey rounded-md px-4 -mx-4 transition-colors duration-300" data-id="element-842">
                                      <div className="flex items-center mb-4 sm:mb-0" data-id="element-843">
                                        <FileText className="h-5 w-5 text-corporate-blue mr-4 flex-shrink-0" data-id="element-844" />
                                        <span className="text-base text-gray-800 font-medium" data-id="element-845">
                                          {doc.name}
                                        </span>
                                      </div>
                                      <a href={doc.url} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center justify-center px-5 py-2.5 border border-corporate-blue text-corporate-blue hover:bg-corporate-blue hover:text-white transition-all duration-500 ease-premium rounded-md text-sm font-medium w-full sm:w-auto hover:scale-[1.02]" data-id="element-846">
                                        <Download className="mr-2 h-4 w-4" data-id="element-847" />
                                        {t('prod.docs.download')}
                                      </a>
                                    </div>)}
                                </div>
                              </div>)}
                          </div> : <div className="px-8 py-10 text-center text-gray-400 font-light text-sm" data-id="element-848">
                            {t('prod.docs.soon')}
                          </div>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}